/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/ingestion_jobs-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-ingestion_jobs-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/ingestion_jobs-entity-config.ts
 * - Run: pnpm run generate:api-clients (regenerates this file)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, data_source_id, triggered_by_user_profile_id, job_type_id, job_status_id, rollback_status_id, rolled_back_by_user_profile_id
 * - Sortable fields: created_at, updated_at
 * - Required fields: id, skip_review, auto_approve_total, auto_approve_completed, auto_approve_failed, auto_approve_errors, skip_transaction_records, updated_at
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/ingestion_jobs-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-ingestion_jobs-api-client.ts (API client)
 * - client/api/generated_schemas/ingestion_jobs.ts (TypeBox schemas and types)
 *
 * Source config: config/entities/ingestion_jobs-entity-config.ts
 * Public fields: 24/24 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { IngestionJobsClient, IngestionJobsQueryOptions } from '../generated_clients/generated-ingestion_jobs-api-client.js';
import {
  IngestionJobsInsert,
  IngestionJobsUpdate,
  IngestionJobsFieldConfig
} from '../../types/generated_schemas/generated-ingestion_jobs-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/ingestion_jobs_business_hooks/

// Query keys with enhanced caching strategy. A list key carries every
// parameter getAll sends, so two different pages never share a cache entry.
export const ingestionJobsKeys = {
  all: ['ingestion_jobs'] as const,
  lists: () => [...ingestionJobsKeys.all, 'list'] as const,
  list: (filters: Record<string, any>, page?: Pick<IngestionJobsQueryOptions, 'limit' | 'offset' | 'sortBy' | 'sortOrder'>) =>
    [...ingestionJobsKeys.lists(), { filters, ...page }] as const,
  details: () => [...ingestionJobsKeys.all, 'detail'] as const,
  detail: (id: string) => [...ingestionJobsKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...ingestionJobsKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...ingestionJobsKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...ingestionJobsKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...ingestionJobsKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useIngestionJobsClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new IngestionJobsClient();
};

/**
 * Hook to get all ingestion_jobs with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with ingestion_jobs data
 */
export const useIngestionJobsList = (options?: IngestionJobsQueryOptions) => {
  const client = useIngestionJobsClient();
  
  return useQuery({
    queryKey: ingestionJobsKeys.list(options?.filters || {}, {
      limit: options?.limit,
      offset: options?.offset,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    }),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single ingestion_job by ID
 *
 * @param id - The ingestion_job ID
 * @returns React Query result with single ingestion_job data
 */
export const useIngestionJobs = (id: string) => {
  const client = useIngestionJobsClient();
  
  return useQuery({
    queryKey: ingestionJobsKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/ingestion_jobs
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/ingestion_jobs_business_hooks/
 */

/**
 * Hook to create a new ingestion_job
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, skip_review, auto_approve_total, auto_approve_completed, auto_approve_failed, auto_approve_errors, skip_transaction_records, updated_at
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating ingestion_job
 */
export const useCreateIngestionJobs = () => {
  const client = useIngestionJobsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: IngestionJobsInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ingestionJobsKeys.all });
    },
  });
};

/**
 * Hook to update an existing ingestion_job
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating ingestion_job
 */
export const useUpdateIngestionJobs = () => {
  const client = useIngestionJobsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: IngestionJobsUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ingestionJobsKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: ingestionJobsKeys.lists() });
    },
  });
};

/**
 * Hook to delete a ingestion_job
 *
 * @returns React Query mutation for deleting ingestion_job
 */
export const useDeleteIngestionJobs = () => {
  const client = useIngestionJobsClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ingestionJobsKeys.all });
    },
  });
};

/**
 * Hook to count ingestion_jobs with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, data_source_id, triggered_by_user_profile_id, job_type_id, job_status_id, rollback_status_id, rolled_back_by_user_profile_id
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useIngestionJobsCount = (filter?: Partial<Record<typeof IngestionJobsFieldConfig.filterable[number], any>>) => {
  const client = useIngestionJobsClient();
  
  return useQuery({
    queryKey: ingestionJobsKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useIngestionJobsFieldConfig = () => {
  return IngestionJobsFieldConfig;
};